// Field-text derivation, shared by the index builders and the Algolia-style
// comparator.
//
// The indexed fields are not all plain record properties: `questionBodies` is
// every question body in a set joined together, `questionChoices` every choice
// body in every question, and so on. The builders compute those when they build
// a document; the comparator needs the same text again at query time to work
// out WHERE in an attribute a term matched.
//
// Both go through this module so there is exactly one definition. A second,
// drifting copy of this logic would make the comparator score positions against
// text the index never actually contained — a difference nothing downstream
// could detect.

/**
 * Join a list of strings, dropping empties.
 *
 * @param {Array<string>} arrayOfStrings
 * @returns {string}
 */
export function joinNonEmpty(arrayOfStrings) {
  if (!Array.isArray(arrayOfStrings)) return ''
  return arrayOfStrings.filter((s) => typeof s === 'string' && s.length > 0).join(' ')
}

/** Every question body in a set, one per element — Algolia's `_questionBodies`. */
export function setQuestionBodyList(set) {
  const questions = (set && Array.isArray(set.questions)) ? set.questions : []
  return questions.map((q) => ((q && typeof q.body === 'string') ? q.body : ''))
}

/** Every question body in a set, joined. */
export function setQuestionBodies(set) {
  return joinNonEmpty(setQuestionBodyList(set))
}

/** Every choice body of every question in a set, one per element. */
export function setQuestionChoiceList(set) {
  const questions = (set && Array.isArray(set.questions)) ? set.questions : []
  const out = []
  for (let i = 0; i < questions.length; i += 1) {
    const q = questions[i]
    if (q && Array.isArray(q.choices)) {
      for (let j = 0; j < q.choices.length; j += 1) {
        const c = q.choices[j]
        if (c && typeof c.body === 'string') out.push(c.body)
      }
    }
  }
  return out
}

/** Every choice body of every question in a set, joined. */
export function setQuestionChoices(set) {
  return joinNonEmpty(setQuestionChoiceList(set))
}

/** Every choice body of a standalone question, one per element. */
export function questionChoiceBodyList(question) {
  const choices = (question && Array.isArray(question.choices)) ? question.choices : []
  return choices.map((c) => ((c && typeof c.body === 'string') ? c.body : ''))
}

/** Every choice body of a standalone question, joined. */
export function questionChoiceBodies(question) {
  return joinNonEmpty(questionChoiceBodyList(question))
}

// Indexed field name → how to get its text back from the source record, per
// document kind. Mirrors the `fields` lists in buildMiniSearchIndices; a field
// indexed but missing here simply yields no position information rather than
// throwing.
const FIELD_TEXT = {
  repo: {
    name: (r) => r.name,
    description: (r) => r.description,
    authorFirstName: (r) => (r.author ? r.author.firstName : ''),
    authorLastName: (r) => (r.author ? r.author.lastName : ''),
  },
  folder: {
    name: (f) => f.name,
  },
  set: {
    name: (s) => s.name,
    // Searchable in Algolia (`attributesToIndex` is unset on sets_questions in
    // production) and absent from the local index until tuned-v6. See §0f.1a.
    description: (s) => s.description,
    questionBodies: setQuestionBodies,
    questionChoices: setQuestionChoices,
  },
  question: {
    body: (q) => q.body,
    choices: questionChoiceBodies,
  },
}

/**
 * The text that was indexed under `fieldName` for this record.
 *
 * @param {Object} original  the source record (`_original` on a hit)
 * @param {string} kind      `_kind`: 'repo' | 'folder' | 'set' | 'question'
 * @param {string} fieldName the indexed field
 * @returns {string} '' when the combination is unknown or empty
 */
export function documentFieldText(original, kind, fieldName) {
  if (!original || !kind) return ''
  const byField = FIELD_TEXT[kind]
  const getter = byField && byField[fieldName]
  if (!getter) return ''
  const text = getter(original)
  return typeof text === 'string' ? text : ''
}

// Per-element text for an indexed field, mirroring how Algolia stores it.
//
// `_questionBodies` and `_questionChoices` are ARRAYS in the Algolia record, and
// Algolia evaluates arrays element by element: word position restarts at 0 in
// each element and proximity never spans two of them. The local index joins them
// into one string for matching, which is harmless there — but scoring positions
// against the joined text puts a term opening question 37 at word ~400 where
// Algolia sees word 0. The comparator uses this instead. See §0f.2.
const FIELD_TEXTS = {
  repo: {
    name: (r) => [r.name],
    description: (r) => [r.description],
    authorFirstName: (r) => [(r.author ? r.author.firstName : '')],
    authorLastName: (r) => [(r.author ? r.author.lastName : '')],
  },
  folder: {
    name: (f) => [f.name],
  },
  set: {
    name: (s) => [s.name],
    description: (s) => [s.description],
    questionBodies: setQuestionBodyList,
    questionChoices: setQuestionChoiceList,
  },
  question: {
    body: (q) => [q.body],
    choices: questionChoiceBodyList,
  },
}

/**
 * The indexed text for `fieldName`, split the way Algolia stores it.
 *
 * @param {Object} original  the source record (`_original` on a hit)
 * @param {string} kind      `_kind`: 'repo' | 'folder' | 'set' | 'question'
 * @param {string} fieldName the indexed field
 * @returns {Array<string>} one entry per array element; a single entry for
 *   scalar fields; empty when the combination is unknown
 */
export function documentFieldTexts(original, kind, fieldName) {
  if (!original || !kind) return []
  const byField = FIELD_TEXTS[kind]
  const getter = byField && byField[fieldName]
  if (!getter) return []
  const texts = getter(original)
  if (!Array.isArray(texts)) return []
  return texts.filter((t) => typeof t === 'string' && t.length > 0)
}
